import { ComponentProps, ReactNode, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';

import { Combobox } from '@/components/core/Combobox';
import { cn } from '@/services/utils';

interface FormFieldProps {
    label: string;
    htmlFor?: string;
    alignTop?: boolean;
    error?: string;
    children: ReactNode;
}

export function FormField({ label, htmlFor, alignTop = false, error, children }: FormFieldProps) {
    return (
        <div className={cn(
            'tw-flex tw-flex-col tw-gap-2 md:tw-flex-row md:tw-gap-3',
            alignTop ? 'md:tw-items-start' : 'md:tw-items-center',
        )}>
            <label
                htmlFor={htmlFor}
                className="tw-m-0 tw-min-w-0 tw-text-t-sm tw-text-t-secondary md:tw-w-2/5 md:tw-shrink-0 md:tw-text-right"
            >
                {label}
            </label>
            <div className="tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-gap-1 md:tw-flex-1">
                {children}
                {error && <span className="tw-text-t-xs lg:tw-text-t-sm tw-text-error">{error}</span>}
            </div>
        </div>
    );
}

export interface SelectOption {
    value: string;
    label: string;
}

export function optionValues(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.map((option) => String((option as SelectOption).value));
}

/**
 * Angular's propsFilter lists names that start with the query before names
 * that only contain it. Cucumber clicks the first match, so `admin` has to
 * come before `demo_user_admin`.
 */
export function rankOptionsByPrefix<T extends SelectOption>(options: T[], query: string): T[] {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
        return options;
    }
    const prefix: T[] = [];
    const rest: T[] = [];
    options.forEach((option) => {
        const label = option.label.toLowerCase();
        const value = option.value.toLowerCase();
        if (label.startsWith(normalized) || value.startsWith(normalized)) {
            prefix.push(option);
        } else {
            rest.push(option);
        }
    });
    return [...prefix, ...rest];
}

export function PrefixRankedCombobox({
    options,
    onInputChange,
    ...props
}: ComponentProps<typeof Combobox>) {
    const [query, setQuery] = useState('');
    const ranked = useMemo(
        () => rankOptionsByPrefix((options ?? []) as SelectOption[], query),
        [options, query],
    );

    return (
        <Combobox
            {...props}
            options={ranked}
            onInputChange={(value, meta) => {
                flushSync(() => setQuery(value));
                return onInputChange?.(value, meta);
            }}
        />
    );
}

/** Keep roles whose name starts with the typed text, matching Angular's prioritized propsFilter bucket. */
export function roleFilterOption(option: { label: string; value: string }, input: string): boolean {
    const query = input.trim().toLowerCase();
    if (!query) {
        return true;
    }
    const label = option.label.toLowerCase();
    const value = String(option.value).toLowerCase();
    return label.startsWith(query) || value.startsWith(query);
}

/** Match a restriction filter by its label or its technical name (for example `tm_us`). */
export function restrictionFilterOption(
    option: { label: string; value: string; data?: SelectOption },
    input: string,
): boolean {
    const query = input.trim().toLowerCase();
    if (!query) {
        return true;
    }
    const label = option.label.toLowerCase();
    const value = String(option.data?.value ?? option.value).toLowerCase();
    return label.includes(query) || value.includes(query);
}

export function filterOptionLabel(option: SelectOption, context: string) {
    if (context !== 'menu' || !option.value) {
        return option.label;
    }
    return (
        <span>
            <span className="tw-block">{option.label}</span>
            <small className="tw-text-t-xs lg:tw-text-t-sm tw-text-t-secondary">{option.value}</small>
        </span>
    );
}
